import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import ConfirmModal from "../common/ConfirmModal";
import {
  fileDocument,
  getDocumentFilings,
  getWorkspaceTree,
  unfileDocument,
} from "../../api/workspaceApi";
import "./workspace.css";

/**
 * The personal layer, shown against an open document: which of MY folders hold it, and a way to put it
 * in one.
 *
 * This is the reverse of the Explorer's question, and it is what stops the personal layer from being a
 * corner of the product you have to remember to visit — the folder a document sits in is visible
 * wherever the document is.
 *
 * Not licence-gated, matching the workspace itself. Filing is a pointer: removing the document from a
 * folder here unfiles it and leaves the archived record untouched — after a confirmation, and into the
 * recycle bin, so the same gesture is as recoverable here as it is in the Explorer.
 */
export default function DocumentFilings({ dbName, archiveNo, documentId }) {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();

  const [filings, setFilings] = useState([]);
  const [folders, setFolders] = useState([]);
  const [picking, setPicking] = useState(false);
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [removing, setRemoving] = useState(null);   // the filing awaiting confirmation

  const canAsk = !!token && !!dbName && (!!archiveNo || documentId != null);

  const reload = useCallback(async () => {
    if (!canAsk) { setFilings([]); return; }
    try {
      const res = await getDocumentFilings(token, { dbName, archiveNo, documentId });
      setFilings(res?.filings || []);
    } catch {
      // A document nobody has filed is the ordinary case; a failure here must stay quiet rather than
      // shouting at someone who is only reading.
      setFilings([]);
    }
  }, [canAsk, token, dbName, archiveNo, documentId]);

  useEffect(() => { setError(""); reload(); }, [reload]);

  // The picker's folder list is fetched once and kept; a language switch renames the starter folders in
  // it, so the cache is dropped rather than left showing the language the user just left.
  useEffect(() => { setFolders([]); }, [i18n?.language]);

  const openPicker = useCallback(async () => {
    setPicking(true);
    setError("");
    if (folders.length === 0) {
      try {
        const tree = await getWorkspaceTree(token, i18n?.language);
        const mine = tree?.folders || [];
        const shared = (tree?.sharedWithMe || []).filter((s) => s.canWrite).map((s) => s.folder);
        setFolders([...mine, ...shared]);
      } catch {
        setFolders([]);
      }
    }
  }, [folders.length, token, i18n?.language]);

  const save = useCallback(async () => {
    if (!choice) return;
    setBusy(true);
    setError("");
    try {
      await fileDocument(token, {
        personalFolderId: Number(choice),
        dbName,
        archiveNo: archiveNo || null,
        documentId: archiveNo ? null : documentId ?? null,
      });
      setPicking(false);
      setChoice("");
      await reload();
    } catch (e) {
      setError(e?.message || t("workspace.fileFailed", { defaultValue: "The document could not be filed." }));
    } finally {
      setBusy(false);
    }
  }, [choice, token, dbName, archiveNo, documentId, reload, t]);

  const remove = useCallback(async () => {
    const target = removing;
    setRemoving(null);
    if (!target) return;
    setBusy(true);
    try {
      await unfileDocument(token, target.filingId);
      await reload();
    } catch (e) {
      setError(e?.message || t("workspace.unfileFailed", { defaultValue: "The document could not be removed from this folder." }));
    } finally {
      setBusy(false);
    }
  }, [removing, token, reload, t]);

  const alreadyIn = useMemo(() => new Set(filings.map((f) => f.folderId)), [filings]);

  if (!canAsk) return null;

  return (
    <div className="dp-section">
      <div className="dp-section-title">
        {t("workspace.myFolders", { defaultValue: "My folders" })}
      </div>

      {error && <div className="ws-error">{error}</div>}

      {filings.length === 0 ? (
        <div className="ws-field__hint" style={{ marginBottom: 8 }}>
          {t("workspace.notFiled", { defaultValue: "This document is not in any of your folders." })}
        </div>
      ) : (
        <div className="ws-share-list">
          {filings.map((f) => (
            <div key={f.filingId} className="ws-share-row">
              <span className="ws-share-row__who" title={f.folderPath}>
                {f.folderPath || f.folderName}
              </span>
              <button
                type="button"
                className="ws-doc__remove"
                title={t("workspace.unfile", { defaultValue: "Remove from this folder" })}
                onClick={() => setRemoving(f)}
                disabled={busy}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {picking ? (
        <div className="dp-actions-row" style={{ alignItems: "center" }}>
          <select
            className="ws-field__select"
            style={{ flex: "1 1 100%", minWidth: 0 }}
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
          >
            <option value="">{t("workspace.chooseFolder", { defaultValue: "Choose a folder…" })}</option>
            {folders
              .filter((f) => !alreadyIn.has(f.id))
              .map((f) => (
                <option key={f.id} value={f.id}>{f.path || f.name}</option>
              ))}
          </select>
          <button className="dp-action-btn primary" onClick={save} disabled={busy || !choice}>
            {t("common.save", { defaultValue: "Save" })}
          </button>
          <button className="dp-action-btn" onClick={() => { setPicking(false); setChoice(""); }} disabled={busy}>
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
        </div>
      ) : (
        /* This button lives in the detail panel, so it wears the panel's button, not the
           workspace page's — otherwise it reads as a stray control under the action bar. */
        <button className="dp-action-btn" onClick={openPicker} disabled={busy}>
          {t("workspace.saveToWorkspace", { defaultValue: "Save to my workspace" })}
        </button>
      )}

      <ConfirmModal
        open={!!removing}
        title={t("workspace.unfile", { defaultValue: "Remove from this folder" })}
        name={removing?.folderPath || removing?.folderName}
        body={t("workspace.unfileBody", {
          defaultValue:
            "The document leaves this folder and goes to your recycle bin, where you can put it back. It stays in the archive either way.",
        })}
        variant="warn"
        icon="🗑️"
        confirmLabel={t("common.remove", { defaultValue: "Remove" })}
        onConfirm={remove}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}
